// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, ReactNode, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import MeetingButton from 'presentations/views/components/molecules/MeetingButton';

import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMaxWithLayoutTheme,
  spacing,
} from 'presentations/views/styles';
import {
  black,
  blue03,
  gray01,
  gray02,
  gray04,
  gray06,
  guest,
  guestPaletone,
  primary,
  white,
} from 'presentations/views/styles/color';
import { formatLocationNameByEvent } from 'presentations/views/helpers/formatter';
import {
  isEventListItem,
  isLocationRemote as isLocationRemoteFunc,
  getEventUserName,
} from 'presentations/views/helpers/event';
import { eventPath } from 'presentations/routes/path';
import Icon from 'presentations/views/components/atoms/Icon';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Emoji from 'presentations/views/components/atoms/Emoji';

import { CalendarEventListItem } from 'presentations/api/schema/calendar_event';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';
import { formatVideoServiceName } from 'presentations/views/helpers/calendar_event';
import { Status } from 'domains/models/event/status';
import { EventListItem } from 'presentations/api/schema/event';
import { AccountCalendarListItem } from 'presentations/api/schema/account';
import { DisplayType } from 'domains/models/user_calendar/displayType';
import { VisibilityType } from 'domains/models/user_calendar/visibilityType';
import { SourceType } from 'domains/models/event_source/sourceType';
import { ProjectListItem } from 'presentations/api/schema/project';
import { findVideoServiceType } from 'domains/models/calendar/videoServiceType';

export interface Props {
  isAllDay?: boolean;
  startedAtString?: string;
  endedAtString?: string;
  mergedEvent?: IntegrationMergedEventListItem;
  isLoading?: boolean;
  accountCalendars?: AccountCalendarListItem[];
  isDone?: boolean;
  onClick?: any;
  project?: ProjectListItem;
  isViewerEvent: boolean;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);

  const [isHoverLocation, setIsHoverLocation] = useState<boolean>(false);
  const toggleHoverLocation = () => setIsHoverLocation(!isHoverLocation);

  const { mergedEvent } = props;

  const isCancel =
    mergedEvent &&
    isEventListItem(mergedEvent.event) &&
    mergedEvent.event.status === Status.Cancel;

  const isLocationRemote =
    mergedEvent && isLocationRemoteFunc(mergedEvent.event);
  const videoService = useMemo(() => {
    if (mergedEvent) {
      const { url, description } = mergedEvent.event;
      const videoServices = findVideoServiceType(url, description);
      return videoServices.length > 0 ? videoServices[0] : null;
    }
    return null;
  }, [mergedEvent?.event]);

  let renderEventDetail: ReactNode | null = null;
  let renderLocation: ReactNode | null = null;
  let renderEventLabel: ReactNode | null = null;
  let color: string | null = null;
  if (
    mergedEvent &&
    mergedEvent.calendarSourceType === CalendarSourceType.Jicoo
  ) {
    const event = mergedEvent.event as EventListItem;
    const isEventType = event.sourceType === SourceType.EventType;
    const isInternalCalnedar = event.sourceType === SourceType.InternalCalendar;
    const isIntegrationCalendar =
      event.sourceType === SourceType.IntegrationCalendar;
    color = event.color;
    let locationLink = '#';
    if (isEventType) {
      locationLink =
        (isLocationRemote ? event.url : eventPath(event.uid)) || '';
    }
    renderEventDetail = (
      <JicooEventLink>
        <Project>
          <Emoji name={props.project?.icon || ''} />
          <ProjectName>{props.project?.name}</ProjectName>
        </Project>
        {isEventType && (
          <EventTypeEvent>
            <JicooEventTagList>
              <JicooEventRoleTag isHost={event.isHost}>
                {t(event.isHost ? 'Host' : 'Guest')}
              </JicooEventRoleTag>
            </JicooEventTagList>
            <JicooEventUserName className="event-name">
              {getEventUserName(event)}
            </JicooEventUserName>
            {event.capacity &&
              event.capacity.limit > 1 &&
              event.capacity.active > 1 && (
                <EventCapacity>
                  {t('Other contacts', {
                    num: event.capacity.active - 1,
                  })}
                </EventCapacity>
              )}
            <JicooEventName>{event.name || t('No Title')}</JicooEventName>
          </EventTypeEvent>
        )}
        {(isInternalCalnedar || isIntegrationCalendar) && (
          <JicooEventUserName className="event-name">
            {event.name || t('No Title')}
          </JicooEventUserName>
        )}
      </JicooEventLink>
    );
    renderLocation = videoService ? (
      <StyledMeetingButton
        videoServiceType={videoService.videoServiceType}
        url={videoService.url}
      />
    ) : (
      event.locationId &&
      locationLink && (
        <EventLocation
          href={locationLink}
          target="_blank"
          onMouseEnter={toggleHoverLocation}
          onMouseLeave={toggleHoverLocation}
          onClick={(e) => {
            e.stopPropagation();

            if (isInternalCalnedar || isIntegrationCalendar) {
              e.preventDefault();
              props.onClick();
            }
          }}
        >
          <EventIcon
            name={isLocationRemote ? 'video' : 'chevron-right'}
            size={fontSize(20)}
          />
          {formatLocationNameByEvent(event)}
        </EventLocation>
      )
    );
  } else if (mergedEvent) {
    const event = mergedEvent.event as CalendarEventListItem;
    let label: string | null = null;
    const displayType: DisplayType | undefined = undefined;
    const visibilityType: VisibilityType | undefined = undefined;
    color = event.color;
    if (
      event.displayType === DisplayType.Private &&
      event.visibilityType === VisibilityType.Busy
    ) {
      label = t('shareOption_1');
    } else if (
      event.displayType === DisplayType.TeamPublic &&
      event.visibilityType === VisibilityType.Busy
    ) {
      label = t('shareOption_2');
    }
    renderEventDetail = (
      <EventName className="event-name">
        <EventNameText>{mergedEvent.event.name || t('No Title')}</EventNameText>
      </EventName>
    );
    renderEventLabel =
      label === null ? null : (
        <EventLabel displayType={displayType} visibilityType={visibilityType}>
          <EventLabelInner
            displayType={displayType}
            visibilityType={visibilityType}
          >
            {label}
          </EventLabelInner>
        </EventLabel>
      );
    renderLocation = videoService ? (
      <StyledMeetingButton
        videoServiceType={videoService.videoServiceType}
        url={videoService.url}
      />
    ) : (
      mergedEvent.event.url && (
        <EventLocationContainer>
          <EventLocation
            href={mergedEvent.event.url}
            target="_blank"
            onMouseEnter={toggleHoverLocation}
            onMouseLeave={toggleHoverLocation}
            onClick={(e) => e.stopPropagation()}
          >
            <EventIcon name={'video'} size={fontSize(20)} />
            {formatVideoServiceName(mergedEvent.event as CalendarEventListItem)}
          </EventLocation>
        </EventLocationContainer>
      )
    );
  }

  if (isCancel) return null;

  if (!mergedEvent || props.isLoading) {
    return (
      <Container isEnabledNameHover={false}>
        <Skeleton variant="rectangular" width="100%" height="22" />
      </Container>
    );
  }

  return (
    <Container isDone={props.isDone} isEnabledNameHover={!isHoverLocation}>
      <Border color={`${color}`} />
      <EventContainer onClick={props.onClick}>
        <EventHeader>
          <EventTime>
            {props.isAllDay
              ? t('All Day')
              : `${props.startedAtString} - ${props.endedAtString}`}
          </EventTime>
          {renderEventLabel}
        </EventHeader>
        <div>{renderEventDetail}</div>
      </EventContainer>
      {renderLocation}
    </Container>
  );
});

const Container = styled.div<{
  isDone?: boolean;
  isEnabledNameHover: boolean;
}>`
  position: relative;
  align-items: center;
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(6)};
  padding: ${spacing(16)};
  cursor: pointer;
  background: ${(props) => (props.isDone ? gray06 : white)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    flex-direction: column;
    align-items: flex-start;
  }
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    padding: ${spacing(12)} ${spacing(16)};
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  :hover .event-name {
    color: ${(props) => (props.isEnabledNameHover ? primary : black)};
  }
`;
const Border = styled.div<{
  color: string | null;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  margin-right: 1px;
  :last-of-type {
    margin-right: 0;
  }
  background-color: ${(props) => props.color};
  border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
  overflow: hidden;
`;
const EventContainer = styled.div`
  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    flex-direction: column;
    align-items: flex-start;
  }
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-size: ${fontSize(12)};
  }
`;
const EventTypeEvent = styled.div``;
const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing(8)};
`;
const EventTime = styled.div`
  display: flex;
  align-items: center;
  min-width: 110px;
  white-space: nowrap;
  text-align: center;

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    justify-content: flex-start;
    width: initial;
    margin-right: 0;
    border-right: none;
    text-align: initial;
  }
`;
const EventName = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(13)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 100%;
    flex-grow: initial;
  }
`;
const EventNameText = styled.div``;
const EventLabel = styled.div<{
  displayType?: DisplayType;
  visibilityType?: VisibilityType;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing(2)} ${spacing(8)};
  border-radius: ${borderRadius(4)};
  border: 1px solid ${gray02};
  box-sizing: border-box;
  background: ${white};
  background: ${(props) =>
    props.displayType === DisplayType.Private &&
    props.visibilityType === VisibilityType.Busy &&
    gray02};
`;

const EventLabelInner = styled.div<{
  displayType?: DisplayType;
  visibilityType?: VisibilityType;
}>`
  font-size: ${fontSize(11)};
  color: ${(props) =>
    props.displayType === DisplayType.Private &&
    props.visibilityType === VisibilityType.Busy
      ? white
      : gray02};
  text-align: center;
`;

const JicooEventLink = styled.div`
  color: ${black};
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 100%;
  }
`;
const JicooEventTagList = styled.div`
  display: flex;
  align-items: center;
`;
const JicooEventRoleTag = styled.div<{ isHost: boolean }>`
  flex-shrink: 0;
  display: inline;
  background-color: ${blue03};
  border-radius: ${borderRadius(4)};
  color: ${primary};
  padding: ${spacing(3)} ${spacing(4)};
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${(props) => !props.isHost && guest};
  background-color: ${(props) => !props.isHost && guestPaletone};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-size: ${fontSize(10)};
  }
`;
const JicooEventTag = styled.div`
  display: flex;
  align-items: center;
`;
const JicooEventTagIcon = styled(Icon)`
  margin-left: ${spacing(8)};
  color: ${gray01};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 14px !important;
    height: 14px !important;
  }
`;
const JicooEventUserName = styled.div`
  margin-top: ${spacing(4)};
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-size: ${fontSize(12)};
  }
`;
const EventCapacity = styled.div`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-size: ${fontSize(10)};
  }
`;
const JicooEventName = styled.div`
  font-size: ${fontSize(12)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-weight: ${fontWeight('bold')};
  }
`;

const EventLocationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing(20)};
  border-top: 1px solid ${gray04};
  padding-top: ${spacing(8)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 100%;
  }
`;
const EventLocation = styled.a`
  display: flex;
  align-items: center;
  width: 126px;
  min-width: 126px;
  font-size: ${fontSize(13)};
  color: ${gray01};
  vertical-align: middle;
  line-height: 20px;

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 50%;
    min-width: initial;
    padding-right: ${spacing(6)};
    margin-left: ${spacing(6)};
    margin-right: 0;
  }

  &:hover {
    color: ${primary};
  }
`;
const EventIcon = styled(Icon)`
  margin-right: ${spacing(6)};
  color: ${primary};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    margin-right: ${spacing(3)};
  }
`;

const Project = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(4)};
  font-weight: ${fontWeight('normal')};
  color: ${gray01};
`;
const ProjectName = styled.span`
  margin-left: ${spacing(4)};
  word-break: all;
`;
const StyledMeetingButton = styled(MeetingButton)`
  margin-top: ${spacing(12)};
`;

export default Component;
